import { ConfigProvider } from "antd";
import { useAuth } from "authentication";
import CircularProgress from "components/CircularProgress";
import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK,
} from "constants/ThemeSetting";
import { setInitUrl } from "data/actions";
import {
  onLayoutTypeChange,
  onNavStyleChange,
  setThemeType,
} from "data/actions/Setting";
import AppLocale from "lngProvider";
import ForgotPassword from "pages/ForgotPassword";
import LandingPage from "pages/LandingPage";
import TermsPage from "pages/LandingPage/terms";
import ResetPassword from "pages/ResetPassword";
import { memo, useEffect } from "react";
import { IntlProvider } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import {
  ClientSelectedProgram,
  isNcdcMode,
  unAuthorizedRoutes,
} from "utils/Constant";
import { getDefaultRoute, validateProgramSelection } from "utils/permissions";
import { getCookieValue, setSessionStorage } from "utils/Storage";
import ProgramSelect from "../ProgramSelect";
import SignIn from "../SignIn";
import SignUp from "../SignUp";
import MainApp from "./MainApp";
import ProtectedRoute from "./ProtectedRoute";

const setLayoutType = (layoutType) => {
  if (layoutType === LAYOUT_TYPE_FULL) {
    document.body.classList.remove("boxed-layout");
    document.body.classList.remove("framed-layout");
    document.body.classList.add("full-layout");
    return;
  }
  if (layoutType === LAYOUT_TYPE_BOXED) {
    document.body.classList.remove("full-layout");
    document.body.classList.remove("framed-layout");
    document.body.classList.add("boxed-layout");
    return;
  }
  if (layoutType !== LAYOUT_TYPE_FRAMED) return;

  document.body.classList.remove("boxed-layout");
  document.body.classList.remove("full-layout");
  document.body.classList.add("framed-layout");
};

const setNavStyle = (navStyle) => {
  if (
    navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
    navStyle === NAV_STYLE_DARK_HORIZONTAL ||
    navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
    navStyle === NAV_STYLE_ABOVE_HEADER ||
    navStyle === NAV_STYLE_BELOW_HEADER
  ) {
    document.body.classList.add("full-scroll");
    document.body.classList.add("horizontal-layout");
  } else {
    document.body.classList.remove("full-scroll");
    document.body.classList.remove("horizontal-layout");
  }
};

const styleSheetLink = document.createElement("link");
styleSheetLink.type = "text/css";
styleSheetLink.rel = "stylesheet";
document.body.appendChild(styleSheetLink);

const App = memo(() => {
  const locale = useSelector(({ settings }) => settings.locale);
  const navStyle = useSelector(({ settings }) => settings.navStyle);
  const layoutType = useSelector(({ settings }) => settings.layoutType);
  const themeType = useSelector(({ settings }) => settings.themeType);
  const isDirectionRTL = useSelector(({ settings }) => settings.isDirectionRTL);
  const { isLoadingUser } = useAuth();

  const dispatch = useDispatch();
  const { authUser } = useSelector(({ auth }) => auth);
  const initURL = useSelector(({ settings }) => settings.initURL);
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  useEffect(() => {
    if (isDirectionRTL) {
      document.documentElement.classList.add("rtl");
      document.documentElement.setAttribute("data-direction", "rtl");
    } else {
      document.documentElement.classList.remove("rtl");
      document.documentElement.setAttribute("data-direction", "ltr");
    }

    // if (themeColor) {
    //   styleSheetLink.href = `/css/${themeColor}.min.css`;
    // }
  }, [isDirectionRTL]);

  useEffect(() => {
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add("dark-theme");
      styleSheetLink.href = "/css/dark_theme.min.css";
    } else if (document.body.classList.contains("dark-theme")) {
      document.body.classList.remove("dark-theme");
      styleSheetLink.href = "";
    }
  }, [themeType]);

  useEffect(() => {
    if (locale) document.documentElement.lang = locale.locale;
  }, [locale]);

  useEffect(() => {
    if (initURL === "") {
      setSessionStorage("initURL", location.pathname);
      dispatch(setInitUrl(location.pathname));
    }
    const params = new URLSearchParams(location.search);

    if (params.has("theme")) {
      dispatch(setThemeType(params.get("theme")));
    }
    if (params.has("nav-style")) {
      dispatch(onNavStyleChange(params.get("nav-style")));
    }
    if (params.has("layout-type")) {
      dispatch(onLayoutTypeChange(params.get("layout-type")));
    }
  }, [location.search, dispatch, initURL, location.pathname]);

  useEffect(() => {
    setLayoutType(layoutType);
    setNavStyle(navStyle);
  }, [layoutType, navStyle]);

  useEffect(() => {
    if (!isLoadingUser) {
      // Not authenticated - redirect to signin
      if (!authUser) {
        const isUnauthorized =
          unAuthorizedRoutes.includes(location.pathname) ||
          location.pathname.startsWith("/reset-password");
        if (!isNcdcMode && !isUnauthorized) {
          history.push("/signin");
        }
        return;
      }

      // User is authenticated - determine where to redirect
      // if (["", ...unAuthorizedRoutes].includes(initURL)) {
      const currentProgram = getCookieValue(ClientSelectedProgram);

      // Validate program selection using centralized system
      const validation = validateProgramSelection(authUser, currentProgram);

      if (!validation.isValid && validation.shouldRedirect) {
        history.push(validation.shouldRedirect);
        return;
      }

      // Get default route for user based on type and program
      const defaultRoute = getDefaultRoute(authUser, currentProgram);

      history.push(defaultRoute);
      //  const authPages = ["", "/", "/signin", "/login", "/signup", "/forgot-password", "/select-program"];
      // const isOnAuthPage = authPages.includes(location.pathname);

      // if (isOnAuthPage) {
      //   const defaultRoute = getDefaultRoute(authUser, currentProgram);
      //   history.push(defaultRoute);
      // }
      // } else {
      //   // User has a saved initURL - navigate to it (unless SuperAdmin, who goes to dashboard)
      //   const userType = getUserType(authUser);

      //   if (userType === UserRoleEnums.SUPER_ADMIN) {
      //     history.push("/administrations/global/dashboard");
      //   } else if (typeof initURL === "string" && initURL !== "") {
      //     setTimeout(() => {
      //       history.push(initURL);
      //     }, 300);
      //   }
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingUser, authUser, initURL, history]);

  const currentAppLocale = AppLocale[locale.locale];
  return isLoadingUser ? (
    <CircularProgress />
  ) : (
    <ConfigProvider
      locale={currentAppLocale.antd}
      direction={isDirectionRTL ? "rtl" : "ltr"}
    >
      <IntlProvider
        locale={currentAppLocale.locale}
        messages={currentAppLocale.messages}
      >
        <Switch>
          {!isNcdcMode && <Route exact path="/" component={LandingPage} />}
          <Route exact path="/terms" component={TermsPage} />
          <Route exact path="/signin" component={SignIn} />
          <Route exact path="/login" component={SignIn} />
          <Route exact path="/signup" component={SignUp} />
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route exact path="/reset-password" component={ResetPassword} />
          <Route exact path="/select-program" component={ProgramSelect} />
          {/* <Route exact path="/file-management/*" component={FileManagement} /> */}
          <ProtectedRoute
            path={`${match.url}`}
            authUser={authUser}
            location={location}
            component={MainApp}
          />
        </Switch>
      </IntlProvider>
    </ConfigProvider>
  );
});

export default App;
